// --- React & Core Dependencies
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
// --- Third-party UI Components
import { Col, Row } from 'antd';
import Input from '@dtci/spellbook/Input';
import Button from '@dtci/spellbook/Button';
import Select from '@dtci/spellbook/Select';
// --- Custom Hooks and Components
import { InputLabel } from '../../../../components/InputLabel/InputLabel';
import { useProductsActions } from '../../../../store/products/useProducts';
import { useLoading } from '../../../../utils/hooks/useLoading';
// --- Utilities and API
import { filterCriteria } from '../../../../utils/filterCriteria';
import { datastageApi } from '../../../../api';
// --- Styles
import './styles.scss';

/**
 * ProductsFilterForm Component
 *
 * This component provides a filter form for products, allowing users to filter
 * products based on various criteria such as product ID, title, year, and status.
 *
 * @returns {Element}
 * @constructor
 */
export function ProductsFilterForm() {
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { updateProducts, updateProductsFilterForm, clearProducts } = useProductsActions();
    const {
        filterForm,
        classOptions,
        legalStatusOptions,
        loadStatusOptions,
        defaultFilterRange,
        productSort,
    } = useSelector((s) => s.products);

    useEffect(() => {
        setTimeout(handleFilter, 5);
    }, []);

    // Fetch products based on filter criteria
    const fetchProducts = async (filter = {}, rangeParam, sortParam) => {
        try {
            return await datastageApi.products(filter, rangeParam, sortParam);
        } catch (error) {
            console.error('Error fetching products:', error);
            return null;
        }
    };

    // Handle filter form submission
    const handleFilter = async () => {
        setLoadingTrue();
        try {
            const data = await fetchProducts(
                filterCriteria(filterForm, '', 'products'),
                defaultFilterRange,
                productSort,
            );
            updateProducts({
                products: data?.content,
                totalPages: data?.totalPages,
                totalItems: data?.totalElements,
                currentPage: data?.pageable?.pageNumber,
            });
        } catch (error) {
            console.error('Error filtering products:', error);
        } finally {
            setLoadingFalse();
        }
    };

    // Handle reset filter action
    const handleResetFilter = async () => {
        setLoadingTrue();
        try {
            clearProducts();
            const data = await fetchProducts({}, defaultFilterRange, productSort);
            updateProducts({
                products: data?.content,
                totalPages: data?.totalPages,
                currentPage: data?.pageable?.pageNumber,
                totalItems: data?.totalElements,
            });
        } catch (error) {
            console.error('Error resetting filter:', error);
        } finally {
            setLoadingFalse();
        }
    };

    return (
        <Row className="products-filter-form">
            {/* Generate input fields dynamically */}
            {[
                { key: 'productId', label: 'Product ID' },
                { key: 'productTitle', label: 'Title' },
                { key: 'startYear', label: 'Start Year' },
                { key: 'endYear', label: 'End Year' },
                { key: 'foxFinancialTitleId', label: 'FOX WPR ID' },
            ].map(({ key, label }) => (
                <Col key={key}>
                    <InputLabel colProps={{ span: 24 }} label={label}>
                        <Input
                            value={filterForm[key]}
                            onChange={(e) =>
                                updateProductsFilterForm({
                                    key: 'filterForm',
                                    data: { [key]: e.target.value },
                                })
                            }
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleFilter();
                                }
                            }}
                            width="auto"
                            id={key}
                            name={key}
                            placeholder="Enter"
                        />
                    </InputLabel>
                </Col>
            ))}

            {/* Generate dropdown fields dynamically */}
            {[
                { key: 'classFilter', label: 'Class', options: classOptions },
                { key: 'loadStatus', label: 'Load Status', options: loadStatusOptions },
                { key: 'legalStatusId', label: 'Legal Status', options: legalStatusOptions },
            ].map(({ key, label, options }) => (
                <Col key={key}>
                    <InputLabel colProps={{ span: 24 }} label={label}>
                        <Select
                            onChange={(value) =>
                                updateProductsFilterForm({
                                    key: 'filterForm',
                                    data: { [key]: value },
                                })
                            }
                            name={key}
                            options={options}
                            isSearchable
                            isClearable
                            value={filterForm[key]}
                        />
                    </InputLabel>
                </Col>
            ))}
            <Col>
                <Button
                    className="filter-button"
                    sizeType="small"
                    styleType="promoted"
                    onClick={handleFilter}
                >
                    Filter
                </Button>
                <Button
                    className="reset-button"
                    sizeType="small"
                    styleType="standard"
                    onClick={handleResetFilter}
                >
                    Reset
                </Button>
            </Col>
        </Row>
    );
}
